// Code generated by apic; DO NOT EDIT.
import type { WsEndpointMeta } from "../types";
import type { SampleContext } from "./shared";
import { resolveAuthMode, hasMTLSLeaf } from "./shared";
import { shellEscapeSQ } from "./curl";
import { goStringLit } from "./go";
import { pyStringLit } from "./python";
import { tsStringLit } from "./typescript";

/**
 * WebSocket sample context: carries the base URL and optional auth credentials.
 * No body/pathParams — WS endpoints always connect to a fixed path.
 */
export interface WsSampleContext {
  /** Base URL of the service (e.g. "https://api.example.com"). */
  baseUrl: string;
  /** Bearer token for jwt / cookie-bearer auth. */
  token?: string;
  /** API key for api_key auth. */
  apiKey?: string;
}

/**
 * Derive the WebSocket URL from the HTTP base URL.
 * https: → wss:, http: → ws:.
 */
function deriveWsUrl(baseUrl: string, path: string): string {
  let base = baseUrl.replace(/\/$/, "");
  if (base.startsWith("https://")) {
    base = "wss://" + base.slice("https://".length);
  } else if (base.startsWith("http://")) {
    base = "ws://" + base.slice("http://".length);
  }
  return base + path;
}

// ── builders ──────────────────────────────────────────────────────────────────

/**
 * wsCurlSample returns a websocat/wscat command for the given WebSocket endpoint.
 *
 * curl does not support WebSocket; we emit the canonical CLI tool (websocat)
 * with a comment noting how auth is passed.
 *
 * Auth over WebSocket: the generated client uses the Sec-WebSocket-Protocol
 * header to carry a "bearer.<token>" sub-protocol for JWT auth. This is the
 * apic convention (websocketProtocols helper in the generated client).
 * For api_key, a "sig.<hmac>" sub-protocol is used. websocat supports passing
 * custom protocols with --protocol.
 */
export function wsCurlSample(ep: WsEndpointMeta, ctx: WsSampleContext): string {
  const wsUrl = deriveWsUrl(ctx.baseUrl, ep.path);
  const mode = resolveAuthMode(ep.auth);
  const composite = ep.auth.composite !== null;
  const mtlsLeaf = hasMTLSLeaf(ep.auth);

  const lines: string[] = [];
  lines.push(`# WebSocket endpoints cannot be called with plain curl.`);
  lines.push(`# Use websocat (https://github.com/vi/websocat) or wscat instead.`);
  lines.push(``);

  if (mode === "jwt" || mode === "cookie") {
    const tok = ctx.token !== undefined && ctx.token !== "" ? ctx.token : "<token>";
    // apic WS auth uses Sec-WebSocket-Protocol: bearer.<base64url-token>
    lines.push(`# JWT auth: the apic client passes the token via a Sec-WebSocket-Protocol sub-protocol.`);
    lines.push(`# websocat encodes it automatically when you supply --protocol:`);
    lines.push(`websocat \\`);
    lines.push(`  --protocol 'apic.ws.v1' \\`);
    lines.push(`  --protocol 'bearer.${shellEscapeSQ(tok)}' \\`);
    lines.push(`  '${shellEscapeSQ(wsUrl)}'`);
    if (composite && mtlsLeaf) {
      lines.push(``);
      lines.push(`# also requires a client certificate (mTLS — configure via --ssl-certificate / --ssl-key)`);
    }
  } else if (mode === "api_key") {
    lines.push(`# api_key auth requires HMAC signing (nonce + timestamp). Cannot be performed from CLI.`);
    lines.push(`# Use the generated TypeScript/Go SDK which handles signing automatically.`);
    lines.push(``);
    lines.push(`# Unauthenticated connect attempt (will be rejected by server):`);
    lines.push(`websocat '${shellEscapeSQ(wsUrl)}'`);
  } else if (mode === "mtls" || mode === "cac" || mode === "piv") {
    lines.push(`# mTLS auth: supply client certificate via --ssl-certificate and --ssl-key.`);
    lines.push(`websocat \\`);
    lines.push(`  --ssl-certificate /path/to/client.pem \\`);
    lines.push(`  --ssl-key /path/to/client-key.pem \\`);
    lines.push(`  '${shellEscapeSQ(wsUrl)}'`);
  } else {
    // public
    lines.push(`websocat '${shellEscapeSQ(wsUrl)}'`);
  }

  if (ep.proto === "json") {
    lines.push(``);
    lines.push(`# Send a JSON message after connecting:`);
    lines.push(`# echo '{}' | websocat '${shellEscapeSQ(wsUrl)}'`);
  } else if (ep.proto === "binary") {
    lines.push(``);
    lines.push(`# This endpoint uses binary frames. Pipe raw bytes into websocat.`);
  }

  return lines.join("\n");
}

/**
 * wsGoSample returns a Go snippet for connecting to a WebSocket endpoint.
 *
 * Uses gorilla/websocket (the de facto Go WS library). The generated
 * apic Go client uses the stdlib net/http upgrade, but gorilla is clearer
 * for a minimal code sample. A prominent comment notes the dependency.
 */
export function wsGoSample(ep: WsEndpointMeta, ctx: WsSampleContext): string {
  const wsUrl = deriveWsUrl(ctx.baseUrl, ep.path);
  const mode = resolveAuthMode(ep.auth);
  const composite = ep.auth.composite !== null;
  const mtlsLeaf = hasMTLSLeaf(ep.auth);

  const lines: string[] = [];
  lines.push(`// requires: go get github.com/gorilla/websocket`);
  lines.push(`package main`);
  lines.push(``);
  lines.push(`import (`);
  lines.push(`\t"fmt"`);
  lines.push(`\t"net/http"`);
  lines.push(``);
  lines.push(`\t"github.com/gorilla/websocket"`);
  lines.push(`)`);
  lines.push(``);
  lines.push(`func main() {`);
  lines.push(`\theader := http.Header{}`);

  if (mode === "jwt" || mode === "cookie") {
    const tok = ctx.token !== undefined && ctx.token !== "" ? ctx.token : "<token>";
    // apic WS auth via Sec-WebSocket-Protocol: bearer.<token>
    lines.push(`\t// apic JWT auth: pass bearer token as Sec-WebSocket-Protocol sub-protocol`);
    lines.push(`\theader.Set("Sec-WebSocket-Protocol", "apic.ws.v1, bearer."+${goStringLit(tok)})`);
    if (composite && mtlsLeaf) {
      lines.push(`\t// also requires mTLS client certificate (configure via tls.Config in Dialer)`);
    }
  } else if (mode === "api_key") {
    lines.push(`\t// api_key auth requires HMAC signing — use the generated apic client instead`);
    lines.push(`\t// (the generated client's connectXxx method handles signing automatically)`);
  } else if (mode === "mtls" || mode === "cac" || mode === "piv") {
    lines.push(`\t// mTLS auth: configure a client certificate in the dialer's TLSClientConfig`);
    lines.push(`\t//   cert, _ := tls.LoadX509KeyPair("client.pem", "client-key.pem")`);
    lines.push(`\t//   dialer := &websocket.Dialer{TLSClientConfig: &tls.Config{Certificates: []tls.Certificate{cert}}}`);
  }

  lines.push(``);
  lines.push(`\tconn, _, err := websocket.DefaultDialer.Dial(${goStringLit(wsUrl)}, header)`);
  lines.push(`\tif err != nil {`);
  lines.push(`\t\tpanic(err)`);
  lines.push(`\t}`);
  lines.push(`\tdefer conn.Close()`);
  lines.push(`\tfmt.Println("connected to ${ep.path}")`);
  lines.push(``);

  if (ep.proto === "json") {
    lines.push(`\t// Send a JSON message:`);
    lines.push(`\tif err := conn.WriteJSON(map[string]any{}); err != nil {`);
    lines.push(`\t\tpanic(err)`);
    lines.push(`\t}`);
    lines.push(``);
    lines.push(`\t// Read a JSON message:`);
    lines.push(`\tvar msg any`);
    lines.push(`\tif err := conn.ReadJSON(&msg); err != nil {`);
    lines.push(`\t\tpanic(err)`);
    lines.push(`\t}`);
    lines.push(`\tfmt.Printf("received: %v\\n", msg)`);
  } else {
    lines.push(`\t// Read a binary message:`);
    lines.push(`\t_, data, err := conn.ReadMessage()`);
    lines.push(`\tif err != nil {`);
    lines.push(`\t\tpanic(err)`);
    lines.push(`\t}`);
    lines.push(`\tfmt.Printf("received %d bytes\\n", len(data))`);
  }

  lines.push(`}`);

  return lines.join("\n");
}

/**
 * wsPythonSample returns a Python websockets snippet for a WebSocket endpoint.
 */
export function wsPythonSample(ep: WsEndpointMeta, ctx: WsSampleContext): string {
  const wsUrl = deriveWsUrl(ctx.baseUrl, ep.path);
  const mode = resolveAuthMode(ep.auth);
  const composite = ep.auth.composite !== null;
  const mtlsLeaf = hasMTLSLeaf(ep.auth);

  const lines: string[] = [];
  lines.push(`# requires: pip install websockets`);
  lines.push(`import asyncio`);
  lines.push(`import websockets`);
  lines.push(``);
  lines.push(`async def main():`);
  lines.push(`    url = ${pyStringLit(wsUrl)}`);

  if (mode === "jwt" || mode === "cookie") {
    const tok = ctx.token !== undefined && ctx.token !== "" ? ctx.token : "<token>";
    // apic WS auth via Sec-WebSocket-Protocol
    lines.push(`    # apic JWT auth: pass bearer token as Sec-WebSocket-Protocol sub-protocol`);
    lines.push(`    subprotocols = [${pyStringLit("apic.ws.v1")}, ${pyStringLit("bearer." + tok)}]`);
    if (composite && mtlsLeaf) {
      lines.push(`    # also requires mTLS client certificate — pass ssl= context with client cert`);
    }
    lines.push(`    async with websockets.connect(url, subprotocols=subprotocols) as ws:`);
  } else if (mode === "api_key") {
    lines.push(`    # api_key auth requires HMAC signing — use the generated SDK instead`);
    lines.push(`    async with websockets.connect(url) as ws:`);
  } else if (mode === "mtls" || mode === "cac" || mode === "piv") {
    lines.push(`    # mTLS auth: pass an ssl.SSLContext with the client certificate`);
    lines.push(`    # import ssl`);
    lines.push(`    # ctx = ssl.create_default_context()`);
    lines.push(`    # ctx.load_cert_chain("/path/to/client.pem", "/path/to/client-key.pem")`);
    lines.push(`    async with websockets.connect(url) as ws:  # add ssl=ctx when using mTLS`);
  } else {
    lines.push(`    async with websockets.connect(url) as ws:`);
  }

  if (ep.proto === "json") {
    lines.push(`        import json`);
    lines.push(`        await ws.send(json.dumps({}))`);
    lines.push(`        message = await ws.recv()`);
    lines.push(`        print(json.loads(message))`);
  } else {
    lines.push(`        data = await ws.recv()`);
    lines.push(`        print(f"received {len(data)} bytes")`);
  }

  lines.push(``);
  lines.push(`asyncio.run(main())`);

  return lines.join("\n");
}

/**
 * wsTsSample returns a TypeScript snippet for connecting to a WebSocket endpoint.
 *
 * When ep.methodName is set, uses the generated client's connectXxx method
 * (preferred). Falls back to a raw WebSocket constructor snippet.
 *
 * Auth: the generated client passes the Bearer token as a Sec-WebSocket-Protocol
 * sub-protocol ("bearer.<token>"), matching the apic WS auth convention.
 */
export function wsTsSample(ep: WsEndpointMeta, ctx: WsSampleContext): string {
  const wsUrl = deriveWsUrl(ctx.baseUrl, ep.path);
  const methodName = ep.methodName !== "" ? ep.methodName : null;
  const connectMethod = methodName !== null ? `connect${methodName}` : null;
  const mode = resolveAuthMode(ep.auth);
  const composite = ep.auth.composite !== null;
  const mtlsLeaf = hasMTLSLeaf(ep.auth);

  const lines: string[] = [];

  if (connectMethod !== null) {
    // ── preferred path: generated client ──────────────────────────────────
    lines.push(`import { createClient } from "./client"; // generated client`);
    lines.push(``);
    lines.push(`async function main() {`);

    const clientOpts: string[] = [`baseUrl: ${tsStringLit(ctx.baseUrl)}`];

    if (mode === "jwt" || mode === "cookie") {
      const tok = ctx.token !== undefined && ctx.token !== "" ? ctx.token : "<token>";
      clientOpts.push(`bearerToken: ${tsStringLit(tok)}`);
      if (composite && mtlsLeaf) {
        lines.push(`  // also requires mTLS client certificate`);
      }
    } else if (mode === "api_key") {
      lines.push(`  // api_key auth: supply an apiKeySigner in ClientConfig`);
    } else if (mode === "mtls" || mode === "cac" || mode === "piv") {
      lines.push(`  // mTLS: configure a client certificate on the transport`);
    }

    lines.push(`  const client = createClient({ ${clientOpts.join(", ")} });`);
    lines.push(`  const stream = await client.${connectMethod}();`);
    lines.push(``);
    lines.push(`  const unsub = stream.onMessage((msg) => {`);
    lines.push(`    console.log("received:", msg);`);
    lines.push(`  });`);

    if (ep.proto === "json") {
      lines.push(``);
      lines.push(`  // Send a message:`);
      lines.push(`  stream.send?.({});`);
    }

    lines.push(``);
    lines.push(`  // Disconnect when done:`);
    lines.push(`  // unsub(); stream.close();`);
    lines.push(`}`);
    lines.push(``);
    lines.push(`main().catch(console.error);`);
  } else {
    // ── fallback: raw WebSocket ────────────────────────────────────────────
    lines.push(`async function main() {`);

    const protocols: string[] = ["apic.ws.v1"];
    if (mode === "jwt" || mode === "cookie") {
      const tok = ctx.token !== undefined && ctx.token !== "" ? ctx.token : "<token>";
      protocols.push(`bearer.${tok}`);
    }

    if (mode === "mtls" || mode === "cac" || mode === "piv") {
      lines.push(`  // mTLS: configure a client certificate on the underlying transport`);
    }
    if (mode === "api_key") {
      lines.push(`  // api_key auth requires HMAC signing — use the generated client instead`);
    }
    if (composite && mtlsLeaf) {
      lines.push(`  // also requires mTLS client certificate`);
    }

    lines.push(`  const ws = new WebSocket(`);
    lines.push(`    ${tsStringLit(wsUrl)},`);
    lines.push(`    ${JSON.stringify(protocols)},`);
    lines.push(`  );`);
    lines.push(``);
    lines.push(`  ws.onopen = () => { console.log("connected"); };`);
    lines.push(`  ws.onmessage = (event) => { console.log("received:", event.data); };`);
    lines.push(`  ws.onerror = (err) => { console.error("error:", err); };`);
    lines.push(`  ws.onclose = () => { console.log("disconnected"); };`);

    if (ep.proto === "json") {
      lines.push(``);
      lines.push(`  // Send a JSON message after open:`);
      lines.push(`  ws.onopen = () => { ws.send(JSON.stringify({})); };`);
    }

    lines.push(`}`);
    lines.push(``);
    lines.push(`main().catch(console.error);`);
  }

  return lines.join("\n");
}

// Re-export SampleContext for convenience.
export type { SampleContext } from "./shared";
